import React, { useEffect, useState, useContext } from 'react'
import getDashboards, { getDashboard, getWidget } from '../../../../../api/getDashboards'
import { Dashboard, DashboardWidgets } from '../../../../../common/types/api/DashboardsResponse'
import { Galleries, WidgetGallery } from '../../../../../common/types/api/page'
import { WidgetTypes } from '../../../../../common/types/api/report'
import Toggle from '../../../../../common/components/SimpleInputs/Toggle'
import { WidgetGalleryTitlePositioning } from './WidgetGalleryTitlePositioning'
import { WidgetGalleryRangeSize } from './WidgetGalleryRangeSize'

import { ComponentCardForm } from '../../../../../common/types/cardForm'
import ListAsync from '../../../LookupsList/ListAsync'
import { OptionType } from '../../../LookupsList/LookupSelect'

import LiveSpan from '../../../Content/LiveSpan'

import { getWidgetPreview } from '../../../../../api/getWidgetPreview'
import Scrollbars from 'react-scrollbars-custom'
import styled from 'styled-components'
import { GroupTypeBase, StylesConfig } from 'react-select'
import { ReportContext } from '../../../../../../src/app'

import i18n from 'live/services/i18n'
import { Recipient } from '../../../../../common/types/form'
import FollowingGlobalSpanIcon from '../../../Content/emailType/FollowinGlobalSpanIcon'

interface HtmlEditor {
    type: WidgetTypes
    config: Galleries
    configProps: {
        indexColumn: number
        indexRow: number
        widthMax: number
        widget: WidgetGallery
    }
}

export default function GalleryEditor({ formObject, onChange }: ComponentCardForm<HtmlEditor>): JSX.Element {
    const { config, type, configProps } = formObject

    const [dashboard, setDashboard] = useState<Dashboard>()
    const [widget, setWidget] = useState<OptionType>()
    const [previewImage, setPreviewImage] = useState<{ imageBase64: string; loading: boolean } | null>(null)

    const indexColumn = configProps?.indexColumn || 0
    const indexRow = configProps?.indexRow
    const columns = config?.columns || []
    const column = columns[indexRow || 0] || []

    const { report } = useContext(ReportContext)
    const recipients = report.mailSettings?.recipients || []
    const [previewRecipient, setPreviewRecipient] = useState<Recipient>(recipients[0])

    const reportWidget = column[indexColumn || 0]

    const modifyGallery = (w: WidgetGallery): void => {
        const newColumns = [...config?.columns]
        newColumns[indexRow][indexColumn] = { ...newColumns[indexRow][indexColumn], ...w }

        onChange({
            type,
            config: {
                ...config,
                columns: newColumns
            },
            configProps
        })
    }

    useEffect(() => {
        const widgetId = reportWidget?.widgetId

        if (widgetId > 0) {
            getWidget(widgetId).then(widgetResponse => {
                if (widgetResponse) {
                    setDashboard({ id: widgetResponse?.dashboard?.id, title: widgetResponse?.dashboard?.name })
                    setWidget({ label: widgetResponse?.title, value: String(widgetResponse?.id) })
                }
            })
        }
    }, [configProps, reportWidget])

    const getMaxWidthSize = (): number => {
        let currentWidgetsSize = 0
        for (let index = 0; index < (config.columns[configProps?.indexColumn || 0]?.length || 0) - 1; index++) {
            currentWidgetsSize += config.columns[configProps?.indexColumn || 0][index].width
        }

        return 900 - currentWidgetsSize
    }

    return (
        <StyledGalleryEditor>
            <div className={'GESettingsSection'}>
                <section className={'GESettingsSection__Header'}>
                    <label>{i18n('Widget Configuration')}</label>
                    <button
                        disabled={!reportWidget?.widgetId}
                        onClick={() => {
                            setPreviewImage({ imageBase64: '', loading: true })
                            getWidgetPreview(reportWidget, previewRecipient)
                                .then(attachments => {
                                    setPreviewImage({ imageBase64: attachments[0].encoded, loading: false })
                                })
                                .catch(err => {
                                    setPreviewImage({ imageBase64: '', loading: false })
                                })
                        }}
                    >
                        <i className={'far fa-redo'}></i>
                        {i18n('Update Preview')}
                    </button>
                </section>
                <section className={'GESettingsSection__Block'}>
                    <label>{i18n('Recipient Group')}</label>
                    <ListAsync<string[]>
                        placeholder={i18n('Select the recipient group')}
                        getApi={function () {
                            return new Promise(resolve => {
                                resolve(recipients.map(recipient => recipient.name))
                            })
                        }}
                        isClearable={true}
                        isMulti={false}
                        value={{ label: previewRecipient?.name, value: previewRecipient?.name }}
                        onChange={option => {
                            if (!option) {
                                setPreviewRecipient(undefined)
                            } else {
                                const firstOption = (option[0] || option) as OptionType
                                const value = firstOption?.value
                                const newRecipient = recipients.find(({ name }) => name === value)
                                if (!newRecipient) {
                                    setPreviewRecipient(undefined)
                                } else {
                                    setPreviewRecipient(newRecipient)
                                }
                            }
                        }}
                        callback={names => names.map(name => ({ label: name, value: name }))}
                        disabled={false}
                        editMode={true}
                        styles={CustomListAsyncStyles}
                    />
                </section>
                <section className={'GESettingsSection__Block'}>
                    <label>
                        {i18n('Dashboard')}
                        <span className={'GESettingsSection__Block--requiredField'}>*</span>
                    </label>
                    <ListAsync<Dashboard[]>
                        placeholder={i18n('Select the dashboard')}
                        getApi={getDashboards}
                        isMulti={false}
                        value={
                            dashboard?.title &&
                            dashboard?.id && { label: dashboard?.title, value: String(dashboard?.id) }
                        }
                        onChange={option => {
                            const firstOption = (option[0] || option) as OptionType
                            setDashboard({
                                id: parseInt(firstOption?.value as string),
                                title: firstOption?.label as string
                            })
                            setWidget(undefined)
                        }}
                        callback={dashboards => dashboards.map(({ id, title }) => ({ label: title, value: `${id}` }))}
                        disabled={false}
                        editMode={true}
                        styles={CustomListAsyncStyles}
                    />
                </section>
                <section className={'GESettingsSection__Block'}>
                    <label>
                        {i18n('Widget')}
                        <span className={'GESettingsSection__Block--requiredField'}>*</span>
                    </label>
                    <ListAsync<DashboardWidgets>
                        placeholder={i18n('Select Widget')}
                        getApi={() => getDashboard(dashboard?.id)}
                        isMulti={false}
                        value={widget}
                        onChange={option => {
                            const firstOption = (option[0] || option) as OptionType
                            setWidget(firstOption)
                            modifyGallery({
                                ...reportWidget,
                                widgetId: firstOption.value as number,
                                widgetName: firstOption.label as string
                            })
                        }}
                        callback={dashboard =>
                            dashboard.widgets.map(({ id, title }) => ({ label: title, value: `${id}` }))
                        }
                        disabled={false}
                        editMode={true}
                        styles={CustomListAsyncStyles}
                    />
                </section>
                <section className={'GESettingsSection__Block'}>
                    <div className={'GESettingsSection__Block--SpanConfig'}>
                        <div className={'GESettingsSection__Block--SpanConfig--LiveSpan'}>
                            <LiveSpan
                                setSpan={span => {
                                    modifyGallery({ ...reportWidget, span })
                                }}
                                span={reportWidget?.span}
                            ></LiveSpan>
                        </div>
                        <div className={'GESettingsSection__Block--SpanConfig--UnfollowGlobalSpanIcon'}>
                            <FollowingGlobalSpanIcon
                                follow={!reportWidget?.unfollowSpan}
                                onClick={() => {
                                    modifyGallery({
                                        ...reportWidget,
                                        unfollowSpan: !reportWidget?.unfollowSpan
                                    })
                                }}
                            />
                        </div>
                    </div>
                </section>
                <section
                    className={'GESettingsSection__Block'}
                    style={{
                        display: 'flex',
                        width: '100%',
                        justifyContent: 'space-between'
                    }}
                >
                    <div className={'GESettingsSection__Block--WidgetDimensionsArea'}>
                        <span style={{ minWidth: '135px', marginRight: '50px' }}>
                            <label>{i18n('Height')}</label>
                            <WidgetGalleryRangeSize
                                min={30}
                                max={923}
                                step={5}
                                value={reportWidget?.height}
                                onChange={value => {
                                    modifyGallery({
                                        ...reportWidget,
                                        height: value
                                    })
                                }}
                            />
                        </span>
                        <span>
                            <label>{i18n('Width')}</label>
                            <WidgetGalleryRangeSize
                                min={100}
                                max={getMaxWidthSize()}
                                step={5}
                                value={reportWidget?.width}
                                onChange={value => {
                                    modifyGallery({
                                        ...reportWidget,
                                        width: value
                                    })
                                }}
                            />
                        </span>
                    </div>
                </section>
                <section className={'GESettingsSection__Block'} style={{ paddingBottom: '0px' }}>
                    <Toggle
                        title={i18n('Widget Title')}
                        value={reportWidget?.showTitle}
                        onChange={value => {
                            modifyGallery({
                                ...reportWidget,
                                showTitle: value
                            })
                        }}
                    />
                    {reportWidget?.showTitle && (
                        <>
                            <label style={{ paddingTop: '22px', paddingBottom: '10px' }}>
                                {i18n('Title Position')}
                            </label>
                            <WidgetGalleryTitlePositioning
                                value={reportWidget.titlePosition || 'center'}
                                onChange={value => {
                                    modifyGallery({
                                        ...reportWidget,
                                        titlePosition: value
                                    })
                                }}
                            />
                        </>
                    )}
                </section>
            </div>
            <div className={'GEPreview'}>
                {previewImage?.loading ? (
                    <div className={'GEPreview__Loading'}>
                        <i className="GEPreview__Loading--Icon fas fa-spinner fa-pulse" />
                    </div>
                ) : (
                    <div
                        className={'GEPreview__Screenshot'}
                        style={{ visibility: !previewImage?.imageBase64 ? 'hidden' : 'visible' }}
                    >
                        <Scrollbars
                            className={'GEPreview__Screenshot--ScrollbarsCustom-Content'}
                            removeTracksWhenNotUsed={true}
                            noScrollY={true}
                            translateContentSizeYToHolder
                            trackXProps={{ style: { width: '100%', left: 0 } }}
                        >
                            {previewImage?.imageBase64 && (
                                <img src={`data:image/jpeg;base64,${previewImage?.imageBase64}`} />
                            )}
                        </Scrollbars>
                    </div>
                )}
            </div>
        </StyledGalleryEditor>
    )
}

const StyledGalleryEditor = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
    height: 100%;
    padding: 10px 10px 0px 10px;
    min-height: 200px;
    background-color: #eeeeee;
    max-height: 75vh;
    overflow: auto;

    .GESettingsSection {
        display: inline;
        flex: 1;
        min-width: 500px;
        width: 500px;
        padding: 10px 10px 0px 10px;
        &__Block {
            margin-bottom: 30px;
            label {
                margin-left: 4px;
                font-style: normal;
                font-weight: 600;
                font-size: 13px;
                line-height: 16px;
                padding-bottom: 5px;
            }
            &--requiredField {
                color: red;
                margin-left: 5px;
            }
            &--WidgetDimensionsArea {
                display: flex !important;
                width: auto;
                height: fit-content;
                flex-direction: row;
                justify-content: space-between;
                align-items: center;
                align-content: center;
            }
            &--SpanConfig {
                display: flex;
                &--LiveSpan {
                    width: 95%;
                }
                &--UnfollowGlobalSpanIcon {
                    justify-content: center;
                    align-items: center;
                    align-self: center;
                    margin: 10px;
                    padding-top: 5px;
                }
            }
        }
        &__Header {
            display: flex;
            flex-direction: 'row';
            justify-content: space-between;
            align-items: center;
            padding: 0px 3px 20px 3px;
            button {
                padding: 5px 10px 5px 10px;
                font-style: normal;
                font-weight: 600;
                font-size: 13px;
                line-height: 16px;
                color: #37a3ef;
                background-color: transparent;
                outline: none;
                border: none;
                transition: 0.2s;
                i {
                    font-style: normal;
                    font-weight: normal;
                    font-size: 11px;
                    line-height: 16px;
                    padding-right: 8px;
                }
                &:hover {
                    background-color: #dddee1;
                }
                &:active {
                    background-color: #c3c3c3;
                }
                &:disabled {
                    color: #a0cbe9;
                    &:hover {
                        background-color: transparent;
                    }
                }
            }
        }
    }

    .GEPreview {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        min-height: 500px;
        border-left: solid 1px #c8ccd4;
        margin-top: 10px;
        margin-bottom: 20px;
        padding: 10px;
        &__Loading {
            &--Icon {
                font-size: 40px;
                color: #a0cbe9;
            }
        }
        &__Screenshot {
            flex: 1;
            display: inline;
            min-height: fit-content;
            max-height: 600px;
            overflow: auto;
            .ScrollbarsCustom-Content {
                display: flex !important;
                align-content: center;
                align-items: center;
            }
        }
    }
`

const CustomListAsyncStyles: StylesConfig<OptionType, boolean, GroupTypeBase<OptionType>> = {
    container: styles => ({
        ...styles,
        // borderRadius: '3px !important',
        borderStyle: 'none !important',
        maxHeight: '38px !important'
    }),
    option: styles => ({
        ...styles,
        fontStyle: 'normal',
        fontWeight: 'normal',
        lineHeight: '14px ',
        fontSize: '14px '
    }),
    control: (styles, state) => ({
        ...styles,
        border: `1px solid ${state.isFocused ? 'var(--blue-500)' : 'var(--gray-500)'} !important`,
        overflow: 'hidden !important',
        borderRadius: '3px !important',
        height: 'auto ',
        maxHeight: '38px !important',
        fontFamily: 'SourceSansPro-Regular,Arial,Helvetica Neue,Helvetica,sans-serif'
    }),
    valueContainer: styles => ({
        ...styles,
        height: 'auto',
        width: 'auto',
        maxHeight: '38px !important'
    }),
    indicatorSeparator: styles => ({
        ...styles,
        display: 'flex !important',
        // // margin: 'auto',
        maxHeight: '22px ! important',
        lineHeight: '8px ! important'
    }),
    indicatorsContainer: styles => ({
        ...styles,
        display: 'flex',
        alignItems: 'center'
    }),
    placeholder: styles => ({
        ...styles,
        position: 'absolute',
        top: '50%'
    }),
    menu: styles => ({
        ...styles,
        borderRadius: '0px',
        fontFamily: 'SourceSansPro-Regular,Arial,Helvetica Neue,Helvetica,sans-serif'
    }),
    clearIndicator: base => ({
        ...base,
        padding: '0px',
        scale: '90%',
        color: 'var(--gray-600)',
        '&:hover': {
            color: 'var(--gray-500)'
        }
    })
}

export { CustomListAsyncStyles, StyledGalleryEditor }
